import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import {
  ArrowRight, Shield, Flame, Landmark, Tent, Cross, HeartPulse, Brain, Handshake, Briefcase, Smile, ExternalLink,
} from "lucide-react";

const BENEFITS = [
  { icon: Briefcase, t: "Career capital", d: "Verified experience, real responsibility and references that carry weight with employers and universities." },
  { icon: Brain, t: "Skills you keep", d: "Leadership, first aid, communication, logistics — learned by doing, not by watching." },
  { icon: Smile, t: "Wellbeing", d: "Regular volunteering is consistently linked with lower stress and higher life satisfaction." },
  { icon: Handshake, t: "Belonging", d: "A network of people who show up — mentors, friends and a community that has your back." },
];

const PATHWAYS = [
  {
    icon: Shield, name: "Army Cadets", ages: "12–18", commitment: "1–2 evenings a week",
    url: "https://armycadets.com",
    desc: "Adventure training, fieldcraft, leadership and qualifications — one of the UK's largest youth organisations. Adult volunteer instructors welcome too.",
    tags: ["leadership", "outdoors", "youth"],
  },
  {
    icon: Flame, name: "Fire Cadets", ages: "13–18", commitment: "Weekly sessions",
    url: "https://www.ukfirecadets.org.uk",
    desc: "Learn firefighting skills, teamwork and community safety alongside serving firefighters — with routes into the fire and rescue service.",
    tags: ["teamwork", "safety", "youth"],
  },
  {
    icon: Landmark, slug: "police-specials", name: "Police Special Constabulary", ages: "18+", commitment: "16+ hours a month",
    url: "https://www.joiningthepolice.co.uk/application-process/ways-in-to-policing/special-constable",
    desc: "Volunteer police officers with full powers, working alongside regulars. Serious responsibility, full training, real frontline impact.",
    tags: ["public service", "responsibility"],
  },
  {
    icon: Tent, name: "Scouts", ages: "4–25 (+adult volunteers)", commitment: "Flexible",
    url: "https://www.scouts.org.uk/volunteer",
    desc: "Help young people gain skills for life — from camping and coding to community projects. Flexible adult volunteering roles at every level.",
    tags: ["youth", "outdoors", "mentoring"],
  },
  {
    icon: Cross, name: "St John Ambulance", ages: "7+ (cadets), 18+ (adults)", commitment: "Regular shifts",
    url: "https://www.sja.org.uk/get-involved/volunteer-with-us/",
    desc: "The nation's first aiders. Train in first aid and provide medical cover at events — from festivals to marathons.",
    tags: ["health", "first aid", "events"],
  },
  {
    icon: HeartPulse, slug: "red-cross", name: "British Red Cross", ages: "15+", commitment: "Flexible",
    url: "https://www.redcross.org.uk/get-involved/volunteer",
    desc: "Emergency response, refugee support, first aid education and community connection — help people in crisis, whoever and wherever they are.",
    tags: ["crisis response", "community", "health"],
  },
];

export default function Pathways() {
  return (
    <div className="bg-cream" data-testid="pathways-page">
      <Seo title="Volunteering pathways" description="The benefits of volunteering, plus established UK pathways — Army Cadets, Fire Cadets, Police Specials, Scouts, St John Ambulance and the Red Cross." />

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="animate-fade-up max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Get inspired</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-forest mt-4 leading-[1.05]">
            So many ways<br />to <span className="shine-text">step up.</span>
          </h1>
          <p className="text-forest/70 mt-5 text-lg max-w-xl">
            Volunteering isn't one thing — it's a thousand different doors. Here's why it's worth it, and some
            of the UK's most established routes in.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.t} delay={i * 0.08}>
              <div data-testid={`pathway-benefit-${i}`} className="bg-white border border-forest/10 rounded-3xl p-7 h-full">
                <div className="w-11 h-11 rounded-2xl bg-terracotta/10 flex items-center justify-center">
                  <b.icon className="w-5 h-5 text-terracotta" />
                </div>
                <div className="font-display text-xl text-forest mt-4">{b.t}</div>
                <div className="text-sm text-neutral-600 mt-2 leading-relaxed">{b.d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pathway cards */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Established routes</div>
          <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-tight tracking-tight">Core voluntary pathways</h2>
          <p className="text-forest/70 mt-4 max-w-xl text-lg">Nationally recognised organisations with structured training, uniforms, ranks and rich role variety — brilliant places to start or deepen a volunteering journey.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {PATHWAYS.map((p, i) => (
            <Reveal key={p.name} delay={(i % 3) * 0.08}>
              <a href={p.url} target="_blank" rel="noreferrer" data-testid={`pathway-card-${p.slug || p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group block bg-white border border-forest/10 rounded-3xl p-8 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-forest/5 flex items-center justify-center">
                    <p.icon className="w-6 h-6 text-forest" />
                  </div>
                  <ExternalLink className="w-4 h-4 text-neutral-300 group-hover:text-forest transition-colors" />
                </div>
                <div className="font-display text-2xl text-forest mt-5">{p.name}</div>
                <div className="text-xs text-neutral-500 mt-1.5">Ages {p.ages} · {p.commitment}</div>
                <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{p.desc}</p>
                <div className="flex flex-wrap gap-1.5 mt-5">
                  {p.tags.map(t => (
                    <Badge key={t} variant="outline" className="rounded-full text-[11px] capitalize border-forest/20 text-forest">{t}</Badge>
                  ))}
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-forest text-white py-16 mt-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl leading-tight tracking-tight">Or start closer to home</h2>
            <p className="text-white/80 mt-3 max-w-lg mx-auto">Local causes on CivicHearth need help today — and every verified hour counts toward your awards.</p>
            <Link to="/opportunities"><Button className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12 mt-7">Browse local opportunities <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
